import { DateTime } from "../Types/DateTime";

/** The type of the phone number, controlling special behaviors or actions taken for it. */
export enum PhoneNumberType {
  /** No action or behavior is applied to this phone number. */
  "No Action" = 1,
  /** Phone numbers of this type are suppressed in new leads. */
  "Suppressed" = 2,
  /** Phone numbers of this type are blocked from reaching the IVR. */
  "Blocked" = 3
}

/** The phone number model from the CRM. */
export class PhoneNumber {
  /** The unique id of the model. */
  id?: string;
  /** The phone number value of the model. */
  value!: string;
  /** The descriptor note for the phone number. */
  note?: string;
  /** The type of behaviors applied to the phone number. */
  type!: PhoneNumberType;
  /** The optional timestamp of the creation of the model. */
  createdAtUtc?: string;
  /** The optional username responsible for the creation of the model. */
  createdByUsername?: string;
  /** The optional timestamp of the most recent update to the model. */
  updatedAtUtc?: string;
  /** The optional username responsible for the most recent update to the model. */
  updatedByUsername?: string;
}

/** An extended phone number model with DateTime objects. */
export class EnhancedPhoneNumber extends PhoneNumber {
  /** The DateTime object for the createdAtUtc prop. */
  createdAt?: DateTime;
  /** The DateTime object for the updatedAtUtc prop. */
  updatedAt?: DateTime;

  constructor(number: PhoneNumber) {
    super();
    this.id = number.id;
    this.value = number.value;
    this.note = number.note;
    this.type = number.type;
    this.createdAtUtc = number.createdAtUtc;
    this.createdByUsername = number.createdByUsername;
    this.updatedAtUtc = number.updatedAtUtc;
    this.updatedByUsername = number.updatedByUsername;
    this.createdAt = number.createdAtUtc ? DateTime.fromISO(number.createdAtUtc) : undefined;
    this.updatedAt = number.updatedAtUtc ? DateTime.fromISO(number.updatedAtUtc) : undefined;
  }

  static toBase(number: EnhancedPhoneNumber): PhoneNumber {
    const { id, value, note, type, createdAtUtc, createdByUsername, updatedAtUtc, updatedByUsername } = number;
    return { id, value, note, type, createdAtUtc, createdByUsername, updatedAtUtc, updatedByUsername };
  }

  static isValid = (value: string) => /^\d{10}$/.test(value);

  static formatValue = (value: string) => {
    const regex = /^(\d{3})(\d{3})(\d{4})$/.exec(value);
    if (regex?.length !== 4) throw "Failed to parse the value to a formatted phone number.";
    return `(${regex[1]}) ${regex[2]}-${regex[3]}`;
  };
}
